import data from "@/public/data/flight/passengers.json";
import {
  Airplane,
  AirplaneLanding,
  AirplaneTakeoff,
  AirTrafficControl,
  CalendarBlank,
} from "@phosphor-icons/react/dist/ssr";
import Typography from "@/shared/components/common/Typography";
import Flex from "@/shared/components/common/Flex";
import Image from "next/image";
import staticData from "@/public/data/flight/results.json";
import React from "react";
import Separator from "@/shared/components/common/separator";
import { Button } from "@/shared/components/common/button";
import { useRouter } from "next/navigation";
import { cn } from "@/shared/libs/commonUtils";
import FlightCounterTimer from "../components/FlightCounterTimer";
import InfoCard from "@/containers/components/cards/infoCard/InfoCard";
import { useDeviceType } from "@/shared/context/DeviceContext";

interface FlightInfoCardProps {
  searchParams: SearchParams;
  flightDetail: FlightDetail;
}

export default function FlightInfoCard({ flightDetail }: FlightInfoCardProps) {
  const router = useRouter();
  const deviceType = useDeviceType();
  const [failedAirlineLogos, setFailedAirlineLogos] = React.useState<
    Set<string>
  >(() => new Set());

  const {
    flightInfo: { title, departTicket, returnTicket },
  } = data;
  const { resultCard } = staticData;

  const flights = Object.values(flightDetail);

  const changeFlightButton = (
    <Button
      disabled
      variant="outline"
      className="md:w-full py-6 max-md:py-2 hover:bg-third shadow-1 hover:shadow-2"
      onClick={() => router.back()}
    >
      <Typography className="font-medium">
        {resultCard.changeFlightLabel}
      </Typography>
    </Button>
  );

  return (
    <InfoCard
      header={{
        title,
        icon: <Airplane weight="fill" className="w-5 h-5 text-secondary" />,
        button: <FlightCounterTimer />,
      }}
      defaultStyle={3}
    >
      <Flex className="flex-col gap-4">
        {flights.map(
          (
            {
              price,
              departure_city_name_lang,
              arrival_city_name_lang,
              airline_logo,
              airline_name_lang,
              arrival_time,
              departure_time,
              jalali_date,
              flight_no,
              type_lang,
              airline_code,
            },
            z,
          ) => (
            <Flex
              key={airline_code}
              className={cn(
                flights.length > 1 ? "basis-1/2 " : "w-full",
                "max-md:flex-col md:border border-border rounded-xs py-3 px-3 md:py-3",
              )}
            >
              <Flex className="md:w-[70%] max-md:w-full flex-col">
                {/* Tags */}
                <Flex className="gap-x-3 items-center justify-between">
                  <Flex className="items-center justify-center gap-x-2 md:hidden">
                    {airline_logo &&
                    airline_logo !== "no logo" &&
                    !failedAirlineLogos.has(airline_logo) ? (
                      <Image
                        src={airline_logo}
                        width={40}
                        height={40}
                        alt="airline"
                        className="w-12 h-12"
                        onError={() =>
                          setFailedAirlineLogos(
                            (failedLogos) =>
                              new Set(failedLogos).add(airline_logo),
                          )
                        }
                      />
                    ) : (
                      <AirTrafficControl className="w-6 h-6 text-secondary" />
                    )}

                    <Flex className="flex-col gap-1">
                      <Flex className="gap-2">
                        <Typography variant="body1" className=" font-normal">
                          {airline_name_lang}
                        </Typography>
                        {flights.length > 1 && (
                          <Flex className="w-max bg-success-10 py-1 px-2 rounded-md items-center">
                            <Typography className="text-sm leading-none! text-success">
                              {z === 0 ? departTicket : returnTicket}
                            </Typography>
                          </Flex>
                        )}
                      </Flex>
                      <Flex className="gap-1">
                        <Typography className="text-muted-foreground text-xs">
                          {type_lang} -
                        </Typography>
                        <Typography className="text-muted-foreground text-xs">
                          {flight_no}
                        </Typography>
                      </Flex>
                    </Flex>
                  </Flex>
                  <Flex className="gap-1 max-md:hidden">
                    <Typography className="text-muted-foreground">
                      {jalali_date}
                    </Typography>
                  </Flex>
                  {deviceType === "mobile" && changeFlightButton}
                </Flex>

                {/* Body */}

                <Flex className="max-md:flex-col p-4 gap-x-8 gap-y-4 justify-center">
                  <Flex className="basis-[10%] max-md:basis-1 flex-col items-center justify-center max-md:hidden">
                    {airline_logo &&
                    airline_logo !== "no logo" &&
                    !failedAirlineLogos.has(airline_logo) ? (
                      <Image
                        src={airline_logo}
                        width={40}
                        height={40}
                        alt="airline"
                        className="w-12 h-12"
                        onError={() =>
                          setFailedAirlineLogos(
                            (failedLogos) =>
                              new Set(failedLogos).add(airline_logo),
                          )
                        }
                      />
                    ) : (
                      <AirTrafficControl className="w-6 h-6 text-secondary" />
                    )}

                    <Typography
                      variant="body1"
                      className="font-normal text-muted-foreground"
                    >
                      {airline_name_lang}
                    </Typography>
                  </Flex>

                  <Flex
                    className={
                      "basis-[90%] max-md:basis-1 gap-x-4 items-center justify-between"
                    }
                  >
                    <Flex className="flex-col items-center">
                      <Typography variant="h5">{departure_time}</Typography>
                      <Typography variant="h6" className="text-ghost">
                        {departure_city_name_lang}
                      </Typography>
                    </Flex>

                    <Flex className="w-full items-center justify-between max-md:relative">
                      <AirplaneTakeoff
                        weight="fill"
                        className="w-5 h-5 md:w-8 md:h-8 text-primary max-md:absolute max-md:-top-4 max-md:right-1 scale-x-[-1]"
                      />
                      <Flex className="w-full! md:w-[80%]! flex-col items-center md:gap-y-2">
                        <Typography className="max-md:hidden">
                          {type_lang}
                        </Typography>
                        <Separator
                          direction="horizontal"
                          className="h-px! md:w-[80%] border-b max-md:border-dashed md:h-[2px]! text-slate-700"
                        />
                        <Typography className="max-md:hidden">
                          {flight_no}
                        </Typography>
                      </Flex>
                      <AirplaneLanding
                        weight="fill"
                        className="w-5 h-5 md:w-8 md:h-8 text-primary max-md:absolute max-md:-top-4  max-md:left-1 scale-x-[-1]"
                      />
                    </Flex>

                    <Flex className="flex-col items-center">
                      <Typography variant="h5">{arrival_time}</Typography>
                      <Typography variant="h6" className="">
                        {arrival_city_name_lang}
                      </Typography>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              <Separator
                direction="horizontal"
                className="md:hidden border-b border-double mb-4 h-px! md:h-[2px]! text-slate-700"
              />
              <Flex className="md:w-[40%]! max-md:w-full md:p-3 max-md:pb-0 flex-col justify-between md:justify-center md:items-center md:h-full md:gap-y-4 md:py-2 bg-transparent max-md:gap-y-2">
                <Flex className="justify-between items-center">
                  <Flex className="px-3">
                    <Flex className="gap-1 md:hidden">
                      <CalendarBlank className="w-5 h-5 text-muted-foreground md:hidden" />
                      <Typography>{jalali_date}</Typography>
                    </Flex>
                  </Flex>
                  <Flex className="gap-x-1 items-center">
                    <Typography
                      variant="h6"
                      className="text-xl font-semibold text-third"
                    >
                      {price.toLocaleString()}
                    </Typography>
                    <Typography className="text-md font-normal text-ghost">
                      {resultCard.currencyLabel}
                    </Typography>
                  </Flex>
                </Flex>

                {deviceType === "desktop" && changeFlightButton}
              </Flex>
            </Flex>
          ),
        )}
      </Flex>
    </InfoCard>
  );
}
